import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { AccVmIpApi, TDeleteAccVmIpResult, TGetAccntResult, TSaveAccntResult } from 'src/services';

import { Accnt, IAccnt, IAccntSnapshot } from '../accnt/Accnt';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { IVm, IVmSnapshot, Vm } from '../vm/Vm';

/**
 * # AccVmIpStore
 *
 * AccVmIpStore을 설명하세요.
 */
export const AccVmIpStore = types
  .model('AccVmIpStore')
  // --------------------------------------------------------------------------
  .props({
    accnt: types.optional(types.array(Accnt), []),
    vm: types.optional(types.array(Vm), []),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setAccnt: (accnt: IAccnt[]) => {
      self.accnt.replace(accnt);
    },
    setVm: (vm: IVm[]) => {
      self.vm.replace(vm);
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    getSections: async (types: Array<'accnt' | 'vm'>) => {
      const accVmIpApi = new AccVmIpApi(self.environment.api);
      const fetchMap = {
        accnt: () => accVmIpApi.getAccnt({ useYn: 'Y' }),
        vm: () => accVmIpApi.getVm({ useYn: 'Y' }),
      };
      const setterMap = {
        accnt: self.setAccnt,
        vm: self.setVm,
      };
      try {
        const promises = types.map(async (type) => {
          const result = await fetchMap[type]();
          self.rootStore.responseStore.getResponseResult(result);

          if (result.kind === 'ok') {
            if (type === 'accnt' && 'accnt' in result) {
              setterMap[type](result.accnt);
            } else if (type === 'vm' && 'vm' in result) {
              setterMap[type](result.vm);
            } else {
              console.error(`get${type} 실패`, result.kind);
            }
          }
        });

        await Promise.all(promises);
      } catch (e) {
        console.error('getSections error:', e);
      }
    },

    getAccnt: async () => {
      const accVmIpApi: AccVmIpApi = new AccVmIpApi(self.environment.api);
      const result: TGetAccntResult = await accVmIpApi.getAccnt();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setAccnt(result.accnt);
      } else {
        console.error(result.kind);
      }
    },
    saveAccnt: async (accnt: IAccntSnapshot) => {
      const accVmIpApi: AccVmIpApi = new AccVmIpApi(self.environment.api);
      const result: TSaveAccntResult = await accVmIpApi.saveAccnt(accnt);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setAccnt([
          ...self.accnt.filter((item) => item.accntSid !== result.accnt.accntSid),
          result.accnt,
        ]);
      } else {
        console.error(result.kind);
      }
    },
    deleteAccnt: async (accntSid: number) => {
      const accVmIpApi: AccVmIpApi = new AccVmIpApi(self.environment.api);
      const result: TDeleteAccVmIpResult = await accVmIpApi.deleteAccnt(accntSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },

    getVm: async () => {
      const accVmIpApi: AccVmIpApi = new AccVmIpApi(self.environment.api);
      const result = await accVmIpApi.getVm();
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setVm(result.vm);
      } else {
        console.error(result.kind);
      }
    },
    saveVm: async (vm: IVmSnapshot) => {
      const accVmIpApi: AccVmIpApi = new AccVmIpApi(self.environment.api);
      const result = await accVmIpApi.saveVm(vm);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setVm([...self.vm.filter((item) => item.vmSid !== result.vm.vmSid), result.vm]);
      } else {
        console.error(result.kind);
      }
    },
    deleteVm: async (vmSid: number) => {
      const accVmIpApi: AccVmIpApi = new AccVmIpApi(self.environment.api);
      const result: TDeleteAccVmIpResult = await accVmIpApi.deleteVm(vmSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TAccVmIpStore = Instance<typeof AccVmIpStore>;
type TAccVmIpStoreSnapshot = SnapshotOut<typeof AccVmIpStore>;

export interface IAccVmIpStore extends TAccVmIpStore {}
export type TAccVmIpStoreKeys = keyof TAccVmIpStoreSnapshot & string;
export interface IAccVmIpStoreSnapshot extends TAccVmIpStoreSnapshot {}
export const createAccVmIpStore = () => types.optional(AccVmIpStore, {} as TAccVmIpStore);
